// Phường Thị P0 (UI_SPEC §18.5): mua / bán nguyên liệu cơ bản, giá cố định. Tiền đi qua ví, hàng
// qua túi; thu tiền bán qua grantReward. Quầy thu linh dược nhận mọi niên phân (giá nhân theo niên phân).
import { batch } from '@preact/signals';
import { BALANCE } from '@data/balance';
import { MARKET, sellPrice, type MarketEntry } from '@data/content/market';
import { isWalletCurrency } from '@data/currencies';
import { goodsDef, isGoodsId, parseStackKey, stackKey, HERB_AGES } from '@data/items/goods';
import { spend, wallet } from './wallet';
import { addGoods, goodsCount, takeGoods } from './inventory';
import { creditWallet } from './wallet';
import { logLine } from './log';
import { markDirty } from '../persistence';

export function marketEntry(id: string): MarketEntry | undefined {
  return MARKET.find((e) => e.id === id);
}

function isHerb(id: string): boolean {
  return isGoodsId(id) && goodsDef(id).kind === 'herb';
}

/** Khóa i18n (dạng tham số `@…`) tên một món / tiền tệ của sạp. */
export function marketNameKey(id: string): string {
  return isWalletCurrency(id) ? `@currency.${id}` : `@${parseStackKey(id).id}.name`;
}

export function owned(id: string): number {
  return isWalletCurrency(id) ? wallet.peek()[id] : goodsCount(id);
}

/** Giá sạp thu một đơn vị của khóa chồng `key` (linh dược theo niên phân); 0 = không thu. */
export function sellPriceOf(key: string): number {
  if (isWalletCurrency(key)) {
    const e = marketEntry(key);
    return e ? sellPrice(e) : 0;
  }
  const { id, age } = parseStackKey(key);
  const e = marketEntry(id);
  if (!e) return 0;
  return isHerb(id) ? sellPrice(e) * BALANCE.HERB_AGE_SELL_MULT[age] : key === id ? sellPrice(e) : 0;
}

/** Các khóa chồng quầy thu đang nhận được từ túi (theo thứ tự sạp, linh dược tách theo niên phân). */
export function sellableKeys(): string[] {
  return MARKET.filter((e) => sellPrice(e) > 0).flatMap((e) =>
    (isHerb(e.id) ? HERB_AGES.map((age) => stackKey(e.id, { age })) : [e.id]).filter((k) => owned(k) > 0),
  );
}

export function buy(id: string, n = 1): boolean {
  const e = marketEntry(id);
  if (!e || e.buy === null || n < 1) return false;
  let ok = false;
  batch(() => {
    ok = spend({ spiritStone: e.buy! * n });
    if (!ok) return;
    if (isWalletCurrency(id)) creditWallet({ [id]: n });
    else addGoods({ [id]: n });
    logLine('world', 'log.market.buy', { item: marketNameKey(id), n, cost: e.buy! * n });
  });
  if (ok) markDirty();
  return ok;
}

/** Bán `n` đơn vị của khóa chồng `key` (vd `herb.dewGrass@century`) hoặc tiền tệ ví. */
export function sell(key: string, n = 1): boolean {
  const price = sellPriceOf(key);
  if (n < 1 || price <= 0) return false;
  let ok = false;
  batch(() => {
    ok = isWalletCurrency(key) ? spend({ [key]: n }) : takeGoods({ [key]: n });
    if (!ok) return;
    creditWallet({ spiritStone: price * n });
    const { age } = parseStackKey(key);
    if (isHerb(parseStackKey(key).id) && age !== 'decade')
      logLine('world', 'log.market.sellAged', {
        item: marketNameKey(key),
        age: `@craft.herbAge.${age}`,
        n,
        gain: price * n,
      });
    else logLine('world', 'log.market.sell', { item: marketNameKey(key), n, gain: price * n });
  });
  if (ok) markDirty();
  return ok;
}
